/**
 * 🛰️ EVERYDAY OS - MASTER TYPE REGISTRY (v2.3 - Hybrid Scoping Build)
 */

import { Timestamp } from 'firebase/firestore';

export enum Role { OWNER = 'OWNER', ADMIN = 'ADMIN', MEMBER = 'MEMBER', GUEST = 'GUEST' }
export enum SafetyMode { STRICT = 'strict', RELAXED = 'relaxed', CHILD = 'child' }
export type TaskStatus = 'backlog' | 'todo' | 'in_progress' | 'done' | 'pantry' | 'history' | 'finalized';
export enum TemplateType { GROCERY = 'grocery', ACTION = 'action', MAINTENANCE = 'maintenance' }
export type PriorityTier = 'P1' | 'P2' | 'P3' | 'none';
export type TaskScope = 'personal' | 'household';
export type TaskSource = 'manual' | 'pet' | 'vehicle' | 'maintenance' | 'system';
export type RecurrenceRangeType = 'days' | 'weeks' | 'months' | 'years' | 'decades';
export type CalendarSource = 'personal' | 'household' | 'custom';
export type FocusLevel = 'deep' | 'shallow' | 'automated'; 
export type SubscriptionModule = 'DASHBOARD' | 'TASKS' | 'CALENDAR' | 'INVENTORY' | 'MAINTENANCE' | 'CONTACTS' | 'AUDIT' | 'SETTINGS';

/**
 * 🛡️ HYBRID SCOPING TYPES
 * Allows the UI to toggle visibility between partitions.
 */
export type DataScope = 'PERSONAL' | 'SHARED' | 'BOTH';

// --- 📞 CONTACTS ---
export type ContactCategory = 'professional' | 'personal' | 'emergency' | 'staff';
export type ContactStream = TaskSource | 'childcare' | 'medical' | 'legal' | 'general';
export interface ContactPhone { label: string; number: string; isPrimary: boolean; }
export interface ContactEmail { label: string; address: string; isPrimary: boolean; }
export interface ContactAddress { street: string; city: string; state: string; zip: string; }

export interface HouseholdContact {
  id: string;
  name: string;
  category: ContactCategory;
  stream: ContactStream;
  subCategory?: string; // Added subCategory property
  phones: ContactPhone[];
  emails: ContactEmail[];
  address?: ContactAddress;
  companyName?: string;
  notes?: string;
  tags: string[];
  isDeleted: boolean;
  localChangeTimestamp: number;
  /** 🛡️ Partition Marker */
  scope: TaskScope; 
  ownerId?: string; 
  licenseNumber?: string; // Added licenseNumber property
  hourlyRate?: number;    // Added hourlyRate property
}

// --- 📐 ENGINES ---
export interface BoardConfig {
  id: string;
  title: string;
  templateType: TemplateType;
  allowedStatuses: TaskStatus[];
  visibility: string;
  isCollapsed: boolean;
  defaultGroupBy: string;
  activeFilters: string[];
}

export interface GroupedTasks { [key: string]: KanbanTask[]; }

export interface CustomCalendar {
  id: string;
  title: string;
  color: string;
  isVisible: boolean;
  ownerId: string;
  collectionName: string;
}

// --- 👤 USER & HOUSEHOLD ---
export interface UserProfile {
  id: string;
  email: string;
  name: string;
  avatar?: string;
  role: Role;
  safetyMode?: SafetyMode;
  acceptedLegalVersion: string;
  acceptedDisclaimerVersion: string;
  legalConsentDate?: string;
  preferredLanguage: string;
  timezone: string;
  autoDetectTz: boolean;
  activeSparkCategories: string[];
  createdAt: string;
  dashboardLayout?: WidgetLayout[];
  cookieConsent?: CookiePreferences;
  activeHouseholdId?: string | null;
  memberOf?: string[];
  defaultScope?: DataScope;
  permissions?: PermissionFlags;
}

export interface CookiePreferences {
  essential: boolean;
  functionality: boolean; // Renamed from 'preferences' for clarity
  analytics: boolean;
  marketing: boolean;
  // Add any other specific cookie categories you track
  lastUpdated?: string;
}

/** 🛡️ Household Permission Matrix */
export interface PermissionFlags {
  canManageTasks: boolean;
  canDeleteTasks: boolean;
  canManageEvents: boolean;
  canManageGroceries: boolean;
  canDeleteGroceries: boolean; // Added canDeleteGroceries property
  canManageInventory: boolean; // Added canManageInventory property
  canManageContacts: boolean; // Added canManageContacts property
  canViewAuditLogs: boolean;   // Added canViewAuditLogs property
  role: Role;                  // Added role property
  joinedAt?: string;
}

export interface HouseholdSubscription {
  activeModules: SubscriptionModule[];
  // Add other subscription-related properties if necessary
}

export interface Household {
  id: string;
  name: string;
  ownerId: string; // Added ownerId property
  memberIds: string[];
  members: HouseholdMember[]; // Added members property
  memberLimit?: number;
  permissions: { [userId: string]: PermissionFlags };
  activeInvite?: {
    code: string;
    expiresAt: string;
    createdBy: string;
    createdAt: string;
  };
  type?: string;
  contacts?: any[]; // 🎯 Ensure this line is present
  address?: { // Also ensure address is defined if used elsewhere
    street: string;
    city: string;
    state: string;
    zip: string;
  } | null;
  subscription?: HouseholdSubscription; // Added subscription property
  // ... other properties you might have
}

export interface HouseholdMember {
  id: string; // Added id property
  userId: string;
  name: string;
  email: string;
  role: Role;
  joinedAt?: string;
  avatar?: string;
}

// --- 🛠️ TASKS & EVENTS ---
export interface KanbanTask {
  id: string;
  title: string;
  description?: string; // ADDED: description property
  status: TaskStatus;
  templateType: TemplateType;
  priorityTier?: PriorityTier; // ADDED: priorityTier property
  energyRequired?: number;
  tags: string[];
  source: TaskSource;
  ownerId?: string;
  createdBy?: string;
  boardId: string;
  lastBoardId: string | null;
  scope: TaskScope;
  isDeleted?: boolean;
  localChangeTimestamp?: number;
  dueDate?: string;
  completedAt?: string;
  assignedContactId?: string;
  lastModifiedBy: string;
  assignedTo: string;
  createdAt: string;
  visibilityRole: Role;
  isLocked: boolean;
  isBaseline: boolean;
  focusLevel: FocusLevel;
  contributionPoints: number;

  subtasks?: SubTask[];
  consumables?: AssetConsumable[];
  toolsRequired?: string[];
  documents?: TaskDocument[];

  estimatedTime: number;
  actualTime: number;
  consumption?: {
    quantity: number;
    remainingQuantity: number;
    unit: string;
    stockLevel?: number;
    averageCycle?: number;
    lastAddedHistory?: string;
    dayBetweenRecurrence?: number[];
    cost?: number;
  };
  recurrence?: { // ADDED: recurrence property
    isEnabled: boolean;
    interval: number;
    frequency: RecurrenceRangeType | null;
    completionTrigger: boolean;
    lastResetDate?: string;
  };
}

export interface SubTask {
  id: string;
  title: string;
  isCompleted: boolean;
  dueDate?: string;
  assignedTo?: string;
}

export interface AssetConsumable {
  id: string;
  name: string;
  quantity: number;
  unit: string;
  lastPurchased?: string;
  costPerUnit?: number;
}

export interface TaskDocument {
  id: string;
  name: string;
  url: string;
  type: string; // e.g., 'pdf', 'image', 'text'
  uploadedAt: string;
}

export interface CalendarEvent {
  id: string;
  title: string;
  description?: string;
  start: string;
  end: string;  
  color: string;
  source: 'personal' | 'household';
  scope: 'personal' | 'household';
  ownerId: string;
  location?: {
    address: string;
    
  };
  assignedContactId?: string;
  isSuggestion?: boolean;
  localChangeTimestamp?: number;
  isSlice?: boolean;
  isContinued?: boolean;
  isContinuation?: boolean;
  travelMetadata?: {
    estimatedDuration: number;
    originType: 'home' | 'previous_event';
    isImpossible: boolean;
    travelBackHomeAdvised: boolean;
  };
  customCalendarId?: string;
  type?: string; // Added type property for filtering in MultiColumnGrid
}

export type WidgetId =
  | 'quick_access'
  | 'calendar_agenda'
  | 'tasks_summary'
  | 'maintenance_agenda'
  | 'activity_log'
  | 'sparks';

export interface LayoutNotification {
  id: string;
  title: string;
  message: string;
  readBy?: string[];
  timestamp: any;
}

export interface WidgetLayout {
  // Define WidgetLayout based on how your dashboardLayout is structured
  // This is a placeholder; adjust according to your actual layout type
  i: string; // ID
  x: number; // X position
  y: number; // Y position
  w: number; // Width
  h: number; // Height
  minW?: number;
  maxW?: number;
  minH?: number;
  maxH?: number;
  static?: boolean;
  isDraggable?: boolean;
  isResizable?: boolean;
}

export interface DailySpark {
  // Define DailySpark structure
  id: string;
  content: string;
  date: string;
}

export interface Intelligence {
  sparks: any[]; // Adjust 'any' to a more specific type if sparks have a defined structure
}

export interface AuditLogEntry {
  id: string;
  userId: string;
  userName: string;
  action: 'CREATE' | 'UPDATE' | 'DELETE' | 'SYNC';
  entityType: 'TASK' | 'EVENT' | 'MEMBER' | 'SYSTEM';
  entityName: string;
  timestamp: number;
  scope: TaskScope;
  metadata?: Record<string, any>;
}

// --- ⚛️ STATE ---
export interface AppState {
  currentUser: UserProfile | null;
  currentHouseholdId: string | null;
  activeDataScope: DataScope; 
  households: Household[];
  tasks: KanbanTask[];
  userProfiles: UserProfile[];
  inventory: KanbanTask[];
  calendarEvents: CalendarEvent[];
  maintenanceTasks: KanbanTask[];
  notifications: LayoutNotification[];
  theme: 'light' | 'dark';
  isBooting: boolean;
  contacts: HouseholdContact[];
  dailySpark: DailySpark | null; // <--- ADDED
  lastSyncTimestamp: number | null; // <--- ADDED, changed to null to match initialState
  intelligence: Intelligence; // <--- ADDED
  auditLogs: AuditLogEntry[]; // <--- ADDED
  dashboardLayout: WidgetLayout[] | null; // <--- ADDED, ensure type matches UserProfile and initialState
}

export type AppAction = 
  | { type: 'SET_USER'; payload: UserProfile | null }
  | { type: 'UPDATE_USER'; payload: Partial<UserProfile> }
  | { type: 'SET_BOOTING'; payload: boolean }
  | { type: 'SET_HOUSEHOLDS'; payload: Household[] }
  | { type: 'SET_CURRENT_HOUSEHOLD'; payload: string | null }
  | { type: 'ADD_HOUSEHOLD'; payload: Household }
  | { type: 'SET_DATA_SCOPE'; payload: DataScope }
  | { type: 'SET_TASKS'; payload: KanbanTask[] }
  | { type: 'ADD_TASK'; payload: KanbanTask }
  | { type: 'UPDATE_TASK'; payload: { id: string; updates: Partial<KanbanTask> } }
  | { type: 'SET_INVENTORY'; payload: KanbanTask[] }
  | { type: 'SET_CALENDAR_EVENTS'; payload: CalendarEvent[] }
  | { type: 'SET_MAINTENANCE_TASKS'; payload: KanbanTask[] }
  | { type: 'SET_NOTIFICATIONS'; payload: LayoutNotification[] }
  | { type: 'SET_THEME'; payload: 'light' | 'dark' }
  | { type: 'TOGGLE_THEME' }
  | { type: 'SET_SYNC'; payload: number | null }
  | { type: 'UPDATE_LAYOUT'; payload: WidgetLayout[] }
  | { type: 'SET_CONTACTS'; payload: HouseholdContact[] };